import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Input, Modal, Select, TextArea } from '../components';
import { api } from '../lib/api';
import { useT } from '../i18n';
import type { TranslationKey } from '../i18n';
import {
  CONFIDENCE_REQUIRED_STAGES,
  nextStageFor,
  type Confidence,
  type Inquiry,
  type InquiryAttachment,
  type InteractionMode,
  type ProductType,
  type Stage,
} from '../types/api';
import type { DndTransitionInput } from '../lib/boardDnd';

const STAGE_LABEL_KEY: Record<Stage, TranslationKey> = {
  VALIDATION: 'stage.validation',
  VERIFICATION: 'stage.verification',
  PRIORITIZATION: 'stage.prioritization',
  REALIZATION: 'stage.realization',
  INTEGRATOR_SELECTION: 'stage.integratorSelection',
  TKP: 'stage.tkp',
  CONTRACT: 'stage.contract',
};

const CONFIDENCE_OPTIONS: { value: Confidence; labelKey: TranslationKey }[] = [
  { value: 'K90', labelKey: 'confidence.k90' },
  { value: 'K50', labelKey: 'confidence.k50' },
  { value: 'K10', labelKey: 'confidence.k10' },
];

const INTERACTION_MODE_OPTIONS: { value: InteractionMode; labelKey: TranslationKey }[] = [
  { value: 'AUDIT', labelKey: 'transitions.modeAudit' },
  { value: 'REMOTE_TZ', labelKey: 'transitions.modeRemoteTz' },
];

const PRODUCT_TYPE_OPTIONS: { value: ProductType; labelKey: TranslationKey }[] = [
  { value: 'INTEGRATION_PROJECT', labelKey: 'transitions.productTypeIntegration' },
  { value: 'EDUCATIONAL_ROBOT', labelKey: 'transitions.productTypeEducational' },
];

export interface InquiriesDndDialogProps {
  inquiry: Inquiry;
  action: 'ADVANCE' | 'DROP' | 'WIN';
  /** Explicit ADVANCE target chosen by the drop column (educational TKP skip). */
  toStage?: Stage;
  /** Must resolve on success and reject (ideally ApiError) on failure. */
  onSubmit: (input: DndTransitionInput) => Promise<void>;
  onClose: () => void;
}

/**
 * Follow-up dialog after a drag & drop gesture that needs extra input:
 * DROP → reason (required), ADVANCE into a confidence-gated stage →
 * confidence rating, WIN → confidence (if unset) + optional reason.
 * Mirrors the TransitionMenu modal contract — including the confidence gate,
 * which is decided by the TARGET stage, not by the action.
 */
export function InquiriesDndDialog({ inquiry, action, toStage, onSubmit, onClose }: InquiriesDndDialogProps) {
  const { t } = useT();
  const [reason, setReason] = useState('');
  const [confidence, setConfidence] = useState<Confidence | ''>(inquiry.confidence ?? '');
  const [interactionMode, setInteractionMode] = useState<InteractionMode | ''>('');
  const [auditAt, setAuditAt] = useState('');
  const [conceptSummary, setConceptSummary] = useState(inquiry.conceptSummary ?? '');
  const [conceptApproved, setConceptApproved] = useState(inquiry.conceptApprovedByClient ?? false);
  const [productType, setProductType] = useState<ProductType | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // First advance VALIDATION → VERIFICATION — pick the product line once.
  const needsProductType =
    action === 'ADVANCE' && inquiry.stage === 'VALIDATION' && !inquiry.productType;
  // Where this ADVANCE actually lands: the explicit drop target («учебный
  // робот» VERIFICATION → ТКП skip) or the next step of the route for the
  // product line — including the one picked right here in the dialog.
  const target =
    action === 'ADVANCE'
      ? toStage ?? nextStageFor(inquiry.stage, productType || inquiry.productType)
      : null;
  // Confidence is only asked for — and only accepted by the server — from
  // PRIORITIZATION onwards, so the target stage decides, not the action:
  // VALIDATION → VERIFICATION must not send it (422 otherwise). Same gate as
  // the in-card TransitionMenu.
  const needsConfidence =
    action === 'ADVANCE'
      ? Boolean(target && CONFIDENCE_REQUIRED_STAGES.includes(target)) && !inquiry.confidence
      : action === 'WIN' && !inquiry.confidence;
  // «Опция возможностей» → «Концепт проекта» mirrors the in-card menu: the
  // work mode is mandatory, the audit date is optional.
  const needsInteractionMode = action === 'ADVANCE' && inquiry.stage === 'PRIORITIZATION';
  // «Концепт проекта» → «Выбор интегратора»: concept text or CONCEPT artifact.
  const needsConcept = action === 'ADVANCE' && inquiry.stage === 'REALIZATION';
  const attachmentsQ = useQuery<InquiryAttachment[]>({
    queryKey: ['inquiries', inquiry.id, 'attachments'],
    queryFn: () => api.get<InquiryAttachment[]>(`/inquiries/${inquiry.id}/attachments`),
    enabled: needsConcept,
  });
  const hasConceptArtifact = (attachmentsQ.data ?? []).some((a) => a.kind === 'CONCEPT');
  const hasStoredConcept = Boolean(inquiry.conceptSummary?.trim()) || hasConceptArtifact;
  const reasonRequired = action === 'DROP';
  const reasonShown = action === 'DROP' || action === 'WIN';

  const title =
    action === 'ADVANCE'
      ? target
        ? t('transitions.advanceTo', { stage: t(STAGE_LABEL_KEY[target]) })
        : t('transitions.advance')
      : action === 'WIN'
        ? t('transitions.win')
        : t('transitions.drop');

  const close = () => {
    if (!submitting) onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const trimmed = reason.trim();
    if (reasonRequired && !trimmed) {
      setError(t('transitions.reasonRequired'));
      return;
    }
    if (needsConfidence && !confidence) {
      setError(
        action === 'ADVANCE' && target
          ? t('transitions.confidenceRequiredAdvance', { stage: t(STAGE_LABEL_KEY[target]) })
          : t('transitions.reasonRequired'),
      );
      return;
    }
    if (needsInteractionMode && !interactionMode) {
      setError(t('transitions.interactionModeRequired'));
      return;
    }
    if (needsProductType && !productType) {
      setError(t('transitions.productTypeRequired'));
      return;
    }
    if (needsConcept && !hasStoredConcept && !conceptSummary.trim()) {
      setError(t('transitions.conceptRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        type: action,
        // Only when the dialog asked for the rating — a stale stored value
        // must not trip the server's early-stage confidence guard.
        ...(needsConfidence && confidence ? { confidence } : {}),
        ...(trimmed ? { reason: trimmed } : {}),
        ...(needsProductType && productType ? { productType } : {}),
        ...(toStage ? { toStage } : {}),
        ...(needsInteractionMode && interactionMode
          ? {
              interactionMode,
              ...(interactionMode === 'AUDIT' && auditAt
                ? { auditAt: new Date(`${auditAt}T00:00:00`).toISOString() }
                : {}),
            }
          : {}),
        ...(needsConcept
          ? {
              ...(conceptSummary.trim() ? { conceptSummary: conceptSummary.trim() } : {}),
              conceptApprovedByClient: conceptApproved,
            }
          : {}),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('inquiries.toastTransitionFail'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={close}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="dnd-transition-form" loading={submitting}>
            {t('transitions.submit')}
          </Button>
        </>
      }
    >
      <form id="dnd-transition-form" onSubmit={submit} noValidate>
        {needsConfidence && (
          <Select
            label={t('transitions.chooseConfidence')}
            value={confidence}
            onChange={(e) => setConfidence(e.target.value as Confidence | '')}
            required
            fullWidth
            placeholder={t('transitions.confidencePlaceholder')}
            options={CONFIDENCE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
          />
        )}
        {needsInteractionMode && (
          <>
            <Select
              label={t('transitions.interactionModeLabel')}
              value={interactionMode}
              onChange={(e) => setInteractionMode(e.target.value as InteractionMode | '')}
              required
              fullWidth
              placeholder={t('transitions.interactionModePlaceholder')}
              options={INTERACTION_MODE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            />
            {interactionMode === 'AUDIT' && (
              <Input
                type="date"
                label={t('transitions.auditDateLabel')}
                value={auditAt}
                onChange={(e) => setAuditAt(e.target.value)}
                fullWidth
              />
            )}
          </>
        )}
        {needsProductType && (
          <Select
            label={t('transitions.productTypeLabel')}
            value={productType}
            onChange={(e) => setProductType(e.target.value as ProductType | '')}
            required
            fullWidth
            placeholder={t('transitions.productTypePlaceholder')}
            options={PRODUCT_TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
          />
        )}
        {needsConcept && (
          <>
            <TextArea
              label={t('transitions.conceptLabel')}
              value={conceptSummary}
              onChange={(e) => setConceptSummary(e.target.value)}
              placeholder={t('transitions.conceptPlaceholder')}
              helperText={hasConceptArtifact ? t('transitions.conceptArtifactHint') : undefined}
              rows={4}
              required={!hasStoredConcept}
              fullWidth
            />
            <Checkbox
              label={t('transitions.conceptApprovedLabel')}
              checked={conceptApproved}
              onChange={(e) => setConceptApproved(e.target.checked)}
            />
          </>
        )}
        {reasonShown && (
          <TextArea
            label={reasonRequired ? t('transitions.reasonPrompt') : t('transitions.reasonOptional')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('transitions.reasonPlaceholder')}
            rows={3}
            required={reasonRequired}
            fullWidth
          />
        )}
        {error && <p role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
